import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import type { ActiveSubscription, LastPayment, Plan } from "./types";

interface CurrentPlanCardProps {
  subscription: ActiveSubscription | null;
  lastPayment: LastPayment | null;
  plans: any;
  currentPlan: Plan | undefined;
}

const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  subscription,
  lastPayment,
  plans,
  currentPlan,
}) => {
  if (!subscription) {
    return (
      <Card
        sx={{
          background:
            "linear-gradient(135deg, #371b17 0%, #5a3a35 50%, #371b17 100%)",
          color: "#fff",
          mb: 4,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h6" sx={{ opacity: 0.8 }}>
            No Active Subscription
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
            Choose a plan below to get started with your restaurant
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const statusConfig = {
    active: {
      label: "Active",
      color: "#4caf50",
      icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    },
    cancelled: {
      label: "Cancelled",
      color: "#f44336",
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    },
    past_due: {
      label: "Past Due",
      color: "#ff9800",
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    },
  };

  const paymentStatusConfig = {
    success: { label: "Paid", color: "#4caf50" },
    failed: { label: "Failed", color: "#f44336" },
    pending: { label: "Pending", color: "#ff9800" },
  };

  const status = statusConfig[subscription.status];
  const formatPrice = (n: number) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);

  return (
    <Card
      sx={{
        background:
          "linear-gradient(135deg, #371b17 0%, #5a3a35 60%, #6b4a45 100%)",
        color: "#fff",
        mb: 4,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -60,
          right: -60,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: -40,
          left: -40,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.03)",
        },
      }}
    >
      <CardContent
        sx={{ p: { xs: 3, md: 4 }, position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ opacity: 0.6, letterSpacing: 2 }}
            >
              Current Plan
            </Typography>
            <Typography variant="h4" sx={{ color: "#fff" }}>
              {plans?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
              {plans?.tagLine}
            </Typography>
          </Box>
          {/* <Chip
            icon={status.icon}
            label={status.label}
            sx={{
              bgcolor: status.color,
              color: "#fff",
              fontWeight: 700,
              "& .MuiChip-icon": { color: "#fff" },
            }}
          /> */}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{ bgcolor: "rgba(255,255,255,0.08)", borderRadius: 3, p: 2 }}
          >
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Billing Amount
            </Typography>
            <Typography variant="h5" sx={{ color: "#fff" }}>
              £{formatPrice(subscription.priceLocked)}
              <Typography
                component="span"
                variant="caption"
                sx={{ opacity: 0.6, ml: 0.5 }}
              >
                /{subscription.billingCycle === "MONTHLY" ? "mo" : "yr"}
              </Typography>
            </Typography>
          </Box>
          {/* <Box
            sx={{ bgcolor: "rgba(255,255,255,0.08)", borderRadius: 3, p: 2 }}
          >
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Commission Rate
            </Typography>
            <Typography variant="h5" sx={{ color: "#fff" }}>
              {currentPlan?.commissionRate ?? "—"}%
            </Typography>
          </Box> */}
          <Box
            sx={{ bgcolor: "rgba(255,255,255,0.08)", borderRadius: 3, p: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AutorenewIcon sx={{ fontSize: 14, opacity: 0.6 }} />
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                Next Billing
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: "#fff" }}>
              {new Date(subscription.currentPeriodEnd).toLocaleDateString(
                "en-IN",
                {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                },
              )}
            </Typography>
          </Box>
        </Box>

        {lastPayment && (
          <>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", mb: 2 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{ opacity: 0.5, letterSpacing: 2, fontSize: 10 }}
              >
                Last Payment
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CreditCardIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      •••• {lastPayment.cardLast4}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {new Date(lastPayment.date).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    £{formatPrice(lastPayment.amount)}
                  </Typography>
                  <Chip
                    label={paymentStatusConfig[lastPayment.status].label}
                    size="small"
                    sx={{
                      bgcolor: paymentStatusConfig[lastPayment.status].color,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 11,
                      height: 22,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentPlanCard;
